import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import './PlayerStatsPanel.css';
import { BASE_URL } from '../config';

const ELLIPSIS = '...';
const shorten = (addr) =>
    typeof addr === 'string' && addr.length > 10
        ? `${addr.slice(0, 6)}${ELLIPSIS}${addr.slice(-4)}`
        : addr ?? '_NoName';

/**
 * PlayerStatsPanel
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - viewerWallet: string (the logged-in user wallet)
 *  - jwt: string
 *  - targetWallet: string (wallet from the leaderboard row)
 */
const PlayerStatsPanel = ({ open, onClose, viewerWallet, jwt, targetWallet }) => {
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');
    const [profile, setProfile] = useState(null);

    const closeBtnRef = useRef(null);
    const sheetRef = useRef(null);

    const titleWallet = useMemo(
        () => shorten((targetWallet || '').toLowerCase()),
        [targetWallet]
    );

    const fetchDetails = useCallback(async () => {
        if (!open || !viewerWallet || !jwt || !targetWallet) return;
        setLoading(true);
        setErr('');
        setProfile(null);

        try {
            const res = await fetch(`${BASE_URL}/get_highscore_details`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    address: (viewerWallet || '').toLowerCase(),
                    highscore_wallet: (targetWallet || '').toLowerCase(),
                    jwt,
                }),
            });

            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const json = await res.json();
            if (json?.valid !== true) throw new Error(json?.reason || 'Invalid response');

            setProfile(json.profile || {});
        } catch (e) {
            console.error('[PlayerStatsPanel] fetch error:', e);
            setErr('Failed to load player profile.');
        } finally {
            setLoading(false);
        }
    }, [open, viewerWallet, jwt, targetWallet]);

    useEffect(() => {
        if (open) fetchDetails();
    }, [open, fetchDetails]);

    // Close on ESC; basic focus management; lock body scroll
    useEffect(() => {
        if (!open) return;

        // focus the Close button for accessibility
        const t = setTimeout(() => closeBtnRef.current?.focus(), 0);

        const onKey = (e) => {
            if (e.key === 'Escape') onClose?.();
        };
        window.addEventListener('keydown', onKey);

        // lock background scroll
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            clearTimeout(t);
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [open, onClose]);

    if (!open) return null;

    const p = profile || {};
    const enemies = p.enemies_kills || {};
    const bosses = p.boss_kills || {};

    const stat = (v) => (typeof v === 'number' ? v : Number(v || 0));

    const dialog = (
        <div
            className="psp-overlay"
            role="dialog"
            aria-modal="true"
            aria-label={`Player stats for ${titleWallet}`}
            onClick={onClose}
        >
            <div
                className="psp-sheet"
                ref={sheetRef}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="psp-header">
                    <div className="psp-title">
                        <span className="psp-title-label">Player</span>
                        <span className="psp-title-wallet" title={targetWallet}>
                            {titleWallet}
                        </span>
                    </div>
                    <button
                        className="psp-close"
                        onClick={onClose}
                        aria-label="Close"
                        ref={closeBtnRef}
                    >
                        CLOSE
                    </button>
                </div>

                {/* Body */}
                <div className="psp-body">
                    {loading ? (
                        <div className="psp-loading">Loading player stats�</div>
                    ) : err ? (
                        <div className="psp-error">{err}</div>
                    ) : (
                        <>
                            {/* Top stats grid */}
                            <div className="psp-stats-grid">
                                <div className="psp-stat-card">
                                    <div className="psp-stat-label">Level</div>
                                    <div className="psp-stat-value gold">Lv {stat(p.account_level)}</div>
                                </div>
                                <div className="psp-stat-card">
                                    <div className="psp-stat-label">Experience</div>
                                    <div className="psp-stat-value">{stat(p.account_experience).toLocaleString()}</div>
                                </div>
                                <div className="psp-stat-card">
                                    <div className="psp-stat-label">Games Played</div>
                                    <div className="psp-stat-value">{stat(p.account_games_played).toLocaleString()}</div>
                                </div>
                                <div className="psp-stat-card">
                                    <div className="psp-stat-label">Deaths</div>
                                    <div className="psp-stat-value">{stat(p.account_deaths).toLocaleString()}</div>
                                </div>
                                <div className="psp-stat-card">
                                    <div className="psp-stat-label">Career Kills</div>
                                    <div className="psp-stat-value">{stat(p.account_career_kills).toLocaleString()}</div>
                                </div>
                                <div className="psp-stat-card">
                                    <div className="psp-stat-label">Career Ammo Used</div>
                                    <div className="psp-stat-value">{stat(p.account_career_ammo_used).toLocaleString()}</div>
                                </div>
                                
                            </div>

                            {/* Two columns: enemies and bosses */}
                            <div className="psp-kills-columns">
                                <div className="psp-kills-card">
                                    <div className="psp-kills-title">Enemy Types Killed</div>
                                    <div className="psp-table">
                                        <div className="psp-thead">
                                            <div>Name</div>
                                            <div className="psp-num">Kills</div>
                                        </div>
                                        <div className="psp-tbody">
                                            {Object.entries(enemies).length === 0 ? (
                                                <div className="psp-empty">No enemy kill data.</div>
                                            ) : (
                                                Object.entries(enemies)
                                                    .sort((a, b) => b[1] - a[1])
                                                    .map(([name, val]) => (
                                                        <div className="psp-row" key={name}>
                                                            <div className="psp-name">{name.replaceAll('_', ' ')}</div>
                                                            <div className="psp-num">{stat(val).toLocaleString()}</div>
                                                        </div>
                                                    ))
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="psp-kills-card">
                                    <div className="psp-kills-title">Boss Kills</div>
                                    <div className="psp-table">
                                        <div className="psp-thead">
                                            <div>Name</div>
                                            <div className="psp-num">Kills</div>
                                        </div>
                                        <div className="psp-tbody">
                                            {Object.entries(bosses).length === 0 ? (
                                                <div className="psp-empty">No boss kill data.</div>
                                            ) : (
                                                Object.entries(bosses)
                                                    .sort((a, b) => b[1] - a[1])
                                                    .map(([name, val]) => (
                                                        <div className="psp-row" key={name}>
                                                            <div className="psp-name">{name.replaceAll('_', ' ')}</div>
                                                            <div className="psp-num">{stat(val).toLocaleString()}</div>
                                                        </div>
                                                    ))
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Meta */}
                            <div className="psp-meta">
                                <div>Created: {p.created_at ? new Date(p.created_at).toLocaleString() : '�'}</div>
                                <div>Updated: {p.last_updated ? new Date(p.last_updated).toLocaleString() : '�'}</div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );

    // Render at the document body so it overlays the entire app
    return createPortal(dialog, document.body);
};

export default PlayerStatsPanel;
